import { DateTimeFormatter } from "@js-joda/core";
import { PageSize } from "api/model/dashboard/DashboardReportOptions";
import { Color, SymbolType } from "types/types";
import { DweenCalendarEnum, DweenTimeUnitEnum } from "./enums";

export const DEFAULT_CALENDAR_FORMAT = "yy-mm-dd";

export const DATE_PATTERN_YEAR = "yyyy";
export const DATE_PATTERN_MONTHS = "yyyy-MM";
export const DATE_PATTERN_DAYS = "yyyy-MM-dd";
export const DATE_PATTERN_HOURS = "yyyy-MM-dd HH";
export const DATE_PATTERN_MINUTES = "yyyy-MM-dd HH:mm";
export const DATE_PATTERN_SECONDS = "yyyy-MM-dd HH:mm:ss";
export const DATE_PATTERN_NO_TIME = "yyyy-MM-dd";

export const DATE_PATTERN_MONTH_DAY_ONLY = "MM-dd";
export const DATE_PATTERN_HOURS_MINUTES_ONLY = "HH:mm";
export const DATE_PATTERN_HOURS_ONLY = "HH";

export const DATE_FORMATTER_YEARS = DateTimeFormatter.ofPattern(DATE_PATTERN_YEAR);
export const DATE_FORMATTER_MONTHS = DateTimeFormatter.ofPattern(DATE_PATTERN_MONTHS);
export const DATE_FORMATTER_DAYS = DateTimeFormatter.ofPattern(DATE_PATTERN_DAYS);
export const DATE_FORMATTER_HOURS = DateTimeFormatter.ofPattern(DATE_PATTERN_HOURS);
export const DATE_FORMATTER_MINUTES = DateTimeFormatter.ofPattern(DATE_PATTERN_MINUTES);
export const DATE_FORMATTER_SECONDS = DateTimeFormatter.ofPattern(DATE_PATTERN_SECONDS);

export const DATE_FORMATTER_NO_TIME = DateTimeFormatter.ofPattern(DATE_PATTERN_NO_TIME);
export const DATE_FORMATTER_MONTH_DAY_ONLY = DateTimeFormatter.ofPattern(DATE_PATTERN_MONTH_DAY_ONLY);
export const DATE_FORMATTER_HOURS_MINUTES_ONLY = DateTimeFormatter.ofPattern(DATE_PATTERN_HOURS_MINUTES_ONLY);
export const DATE_FORMATTER_HOURS_ONLY = DateTimeFormatter.ofPattern(DATE_PATTERN_HOURS_ONLY);

export const datePaternOptions = [
  { label: DATE_PATTERN_YEAR, value: DATE_PATTERN_YEAR },
  { label: DATE_PATTERN_MONTHS, value: DATE_PATTERN_MONTHS },
  { label: DATE_PATTERN_DAYS, value: DATE_PATTERN_DAYS },
  { label: DATE_PATTERN_HOURS, value: DATE_PATTERN_HOURS },
  { label: DATE_PATTERN_MINUTES, value: DATE_PATTERN_MINUTES },
  { label: DATE_PATTERN_SECONDS, value: DATE_PATTERN_SECONDS },
];

export const taskExecutorQueues = [
  {
    value: "DEFAULT",
    label: "Default",
  },
  {
    value: "FAST_COMPUTE",
    label: "Fast Compute",
  },
  {
    value: "TESTING",
    label: "Testing",
  },
];

export const DEFAULT_VIEW = "/views/easasindex.xhtml";
export const DEFAULT_DASHBOARD_URL = "/EASAS/views/dataservices/dataservicedashboard.xhtml?";
export const DEFAULT_REPORT_URL = "/EASAS/views/dataservices/dashboard-report.xhtml?";
export const HASH_ROUTER_URL = "/EASAS/ui/#";

export const CSV_TYPE = "text/csv;charset=utf-8,";

export const BAR_CHART_DATA_POINTS_LIMIT = 100;

export const EASAS_JS_SCRAPER_URL = () => {
  return process.env.EASASJS_VERSIONS_SCRAPER_URL!!;
};

export const EASAS_REST_URL = () => {
  return `${window.contextRoot}/rest`;
};

export const symbols: SymbolType[] = [
  {
    name: "Circle",
    char: "⬤",
    decCode: "&#11044;",
    plotlySymbol: "circle",
  },
  {
    name: "Diamond",
    char: "◆",
    decCode: "&#9670;",
    plotlySymbol: "diamond",
  },
  {
    name: "X",
    char: "🞬",
    decCode: "&#128940;",
    plotlySymbol: "x",
  },
  {
    name: "Triangle left",
    char: "◀",
    decCode: "&#9664;",
    plotlySymbol: "triangle-left",
  },
  {
    name: "Triangle right",
    char: "▶",
    decCode: "&#9654;",
    plotlySymbol: "triangle-right",
  },
  {
    name: "Star",
    char: "🟊",
    decCode: "&#128970;",
    plotlySymbol: "star",
  },
];

export const colors: Color[] = [
  {
    name: "Heat",
    hex: "#D95648",
  },
  {
    name: "PET",
    hex: "#E0C05B",
  },
  {
    name: "BMS",
    hex: "#2BBD66",
  },
  {
    name: "Air",
    hex: "#616BF2",
  },
  {
    name: "Dry",
    hex: "#E97451",
  },
  {
    name: "Boiler",
    hex: "#9ACD32",
  },
  {
    name: "Cold",
    hex: "#46BBC2",
  },
  {
    name: "Waste",
    hex: "#B358C9",
  },
  {
    name: "Blue-600",
    hex: "#003E79",
  },
  {
    name: "Blue-500",
    hex: "#005EB5",
  },
  {
    name: "Blue-400 primary",
    hex: "#007DF2",
  },
  {
    name: "Blue-300",
    hex: "#2997FF",
  },
  {
    name: "Blue-200",
    hex: "#5EB1FF",
  },
  {
    name: "Blue-100",
    hex: "#94CBFF",
  },
  {
    name: "Black",
    hex: "#000000",
  },
  {
    name: "Grey-700",
    hex: "#2E3A3C",
  },
  {
    name: "Grey-600",
    hex: "#5C7577",
  },
  {
    name: "Grey-500",
    hex: "#ABBCBE",
  },
  {
    name: "Grey-400 primary",
    hex: "#CED8D9",
  },
  {
    name: "Grey-300",
    hex: "#DAE2E3",
  },
  {
    name: "Grey-200",
    hex: "#E7EBEC",
  },
  {
    name: "Grey-100",
    hex: "#F3F5F6",
  },
  {
    name: "White",
    hex: "#FFFFFF",
  },
];

export const textSizes = ["xs", "sm", "base", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl", "7xl", "8xl"];

export const reactQuillModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    ["blockquote", "code-block"],
    [{ header: 1 }, { header: 2 }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ script: "sub" }, { script: "super" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ direction: "rtl" }],
    [{ size: ["small", false, "large", "huge"] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ["clean"],
  ],
};
export const aligns = ["left", "right", "top", "bottom", "center"] as const;

export const dweenColorway = [
  "#007DF2", // Blue-400 primary
  "#E97451", // Dry (orange)
  "#2BBD66", // BMS (green)
  "#D95648", // HEAT (red)
  "#B358C9", // Waste (purple)
  "#E0C05B", // PET (yellow)
  "#616BF2", // Air (blue-purple)
  "#5C7577", // Grey-600
  "#9ACD32", // Boiler (green-yellow)
  "#46BBC2", // Cold (teal)
];

export const dweenTimeUnitOptions = [
  { label: "Millis", value: DweenTimeUnitEnum.MILLIS },
  { label: "Seconds", value: DweenTimeUnitEnum.SECONDS },
  { label: "Minutes", value: DweenTimeUnitEnum.MINUTES },
  { label: "Halfhour", value: DweenTimeUnitEnum.HALFHOUR },
  { label: "Hours", value: DweenTimeUnitEnum.HOURS },
  { label: "Days", value: DweenTimeUnitEnum.DAYS },
  { label: "Weeks", value: DweenTimeUnitEnum.WEEKS },
  { label: "Months", value: DweenTimeUnitEnum.MONTHS },
  { label: "Years", value: DweenTimeUnitEnum.YEARS },
];

export const dweenTimeUnitOptionsWithFourFourFive = [
  ...dweenTimeUnitOptions,
  { label: "4-4-5 Calendar", value: DweenCalendarEnum.CALENDAR_445 },
];

export const PAGE_WIDTHS: Record<PageSize, number> = {
  A5: 559,
  A4: 718,
  A3: 1047,
  A2: 1512,
  A1: 2169,
  A0: 2794,
};
